import React from 'react';

import { useTranslation } from '@wix/yoshi-flow-editor';
import { useStyles } from '@wix/tpa-settings/react';

import { messagesModel } from '@/features';

import styleParams from 'AiAssistantWidget/stylesParams';

import { ScrollingGallery } from '../ScrollingGallery';
import { Product } from '../Product';
import { PagePayload as Page } from '../PagePayload';
import { FormSubmittedPayload } from '../FormSubmittedPayload';
import { GenericPayload } from '../GenericPayload';
import { SuggestedQuestionsPayload } from '../SuggestedQuestionsPayload/SuggestedQuestionsPayload';
import { FaqQuestionPayload } from '../FaqQuestionPayload/FaqQuestionPayload';
import { FilePayload } from '../FilePayload';

import { PayloadAction } from './types';
import { st, classes } from './ChatMessagePayload.st.css';
import { CardTemplatePayload } from './CardTemplatePayload';
import { MinimalTemplatePayload } from './MinimalTemplatePayload';
import { WhatsAppSocialButtonPayload } from './WhatsAppSocialButtonPayload';

type ChatPayload = {
  id: string;
  type: string;
  title?: string;
  subtitle?: string;
  description?: string;
  img?: string;
  url?: string;
  infoText?: string; // for whatsappSocialButton
  price?: string;
  original_price?: string;
  email?: string;
  name?: string;
  phone?: string;
  actions?: PayloadAction[];
  displayName?: string; // for file_uploaded
  sizeInBytes?: number; // for file_uploaded
  mediaType?: 'IMAGE' | 'DOCUMENT'; // for file_uploaded
  file?: File; // for file_uploaded optimistic preview
  fileUploadError?: string; // for handling file_uploaded error
};

const PayloadRenderer: React.FC<{
  payload: ChatPayload;
  isSmall: boolean;
  isMultiple?: boolean;
  isProductsWithOneSitePage?: boolean;
  itemPosition?: number;
  totalItems?: number;
}> = ({
  payload,
  isSmall,
  itemPosition: current,
  totalItems: total,
  isMultiple = false,
  isProductsWithOneSitePage = false,
}) => {
  const {
    type,
    id,
    img,
    title,
    price,
    original_price,
    url,
    email,
    name,
    phone,
    actions,
    displayName,
    sizeInBytes,
    mediaType,
    file,
    fileUploadError,
    infoText,
  } = payload;
  const { t } = useTranslation();

  switch (type) {
    case 'products':
      return (
        <Product
          payloadId={id}
          isSmall={isSmall}
          images={[img ?? '']}
          title={title}
          price={price}
          originalPrice={original_price}
          link={url}
          actions={actions}
          noImagesGallery={isMultiple}
          aria-label={`${title ?? ''} ${
            price ? t('app.widget.ariallabel.price', { price }) + ',' : ''
          }  ${
            current &&
            total &&
            t('app.widget.ariallabel.cardPosition', {
              current,
              total,
            })
          }`}
        />
      );

    case 'site_pages':
      return (
        <Page
          payloadId={id}
          isSmall={isSmall}
          image={img}
          title={title}
          link={url}
          isMultiple={isMultiple}
          isProductsWithOneSitePage={isProductsWithOneSitePage}
        />
      );

    case 'form_submitted':
      return (
        <FormSubmittedPayload
          isSmall={isSmall}
          email={email}
          name={name}
          phone={phone}
        />
      );
    case 'suggested_question':
      return <SuggestedQuestionsPayload title={title} />;

    case 'faq_question':
      return <FaqQuestionPayload id={id} title={title} />;

    case 'file_uploaded':
      return (
        <FilePayload
          title={displayName ?? file?.name}
          size={sizeInBytes ?? file?.size}
          url={url}
          mediaType={mediaType}
          error={fileUploadError}
        />
      );

    case 'minimal': {
      const opts: messagesModel.MinimalTemplate = payload as any;
      return <MinimalTemplatePayload text={opts.text} iconUrl={opts.iconUrl} />;
    }
    case 'card': {
      const opts: messagesModel.CardTemplate = payload as any;
      return (
        <CardTemplatePayload
          title={opts.title}
          buttons={opts.buttons}
          orientation={opts.orientation}
          icon={opts.icon}
          textLines={opts.textLines}
          imageUrl={opts.imageUrl}
        />
      );
    }
    case 'whatsappSocialButton':
      return <WhatsAppSocialButtonPayload infoText={infoText} url={url} />;

    case 'media':
      const opts: messagesModel.WixUserMedia = payload as any;
      return (
        <FilePayload
          title={opts.fileName ?? undefined}
          size={opts.fileSizeInBytes ?? undefined}
          url={opts.url ?? undefined}
          mediaType={opts.mimeType?.startsWith('image/') ? 'IMAGE' : undefined}
          error={fileUploadError}
        />
      );

    default:
      return <GenericPayload isSmall={isSmall} payload={payload} />;
  }
};

export const ChatMessagePayload: React.FC<{
  additionalData: Record<string, any>[];
}> = ({ additionalData }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const aspectRatio = styles.get(styleParams.productImageAspectRatio);

  if (!additionalData?.length) {
    return null;
  }

  const payloadsByType = additionalData.reduce((acc, payload) => {
    const type = payload.type || 'unknown';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(payload);
    return acc;
  }, {} as Record<string, ChatPayload[]>);

  const isProductsWithOneSitePage = Boolean(
    payloadsByType.products?.length && payloadsByType.site_pages?.length === 1,
  );

  const isSingleProduct = Boolean(payloadsByType.products?.length === 1);

  return (
    <>
      {Object.entries(payloadsByType).map(
        ([type, payloads]: [string, ChatPayload[]]) => {
          const isMultiple = payloads.length > 1;
          const isCardPayloads = payloads.some(
            (payload) => 'url' in payload && 'img' in payload,
          );
          const isProducts = type === 'products';
          const isPages = type === 'site_pages';
          const isQuestions = type === 'suggested_question';
          const isFaqQuestions = type === 'faq_question';
          const needsGallery = isCardPayloads && isMultiple && !isPages;
          const needsLinksLayout = isPages && isMultiple;

          const payloadItems = payloads.map((payload: ChatPayload, index) => (
            <div
              key={payload.id}
              className={st(
                needsGallery ? classes.galleryPayload : classes.payload,
              )}
            >
              <PayloadRenderer
                payload={payload}
                isSmall={
                  needsGallery || (isProducts && !isMultiple) || isSingleProduct
                }
                isMultiple={isMultiple}
                isProductsWithOneSitePage={isProductsWithOneSitePage}
                itemPosition={index + 1}
                totalItems={payloads.length}
              />
            </div>
          ));

          if (needsGallery) {
            return (
              <div key={type} className={st(classes.cardPayload)}>
                <ScrollingGallery
                  arialLabel={t('app.widget.ariallabel.items')}
                  arrowPosition={aspectRatio === 1 ? 128 : 78}
                  totalItems={payloads.length}
                >
                  {payloadItems}
                </ScrollingGallery>
              </div>
            );
          }

          if (needsLinksLayout || isQuestions || isFaqQuestions) {
            return (
              <div key={type} className={st(classes.linksPayload)}>
                {payloadItems}
              </div>
            );
          }

          return (
            <div
              key={type}
              className={st(
                isCardPayloads && isMultiple
                  ? classes.cardPayload
                  : classes.payload,
              )}
            >
              {payloadItems}
            </div>
          );
        },
      )}
    </>
  );
};
